/**
 * Fire-and-forget client diagnostic reporter (XT-413).
 *
 * Posts to the API raw endpoint `POST /client-events` so BE logs capture
 * chunk-load failures / reloads even when:
 * - Clerk auth is broken
 * - the tRPC client is mid-reload
 * - the page is navigating away (sendBeacon / keepalive)
 *
 * Debounced per kind so a reload loop (or multi-tab storm) does not burn
 * the server's per-IP rate limit (30/min) on duplicate beacons.
 */

export type ClientEventKind =
  | 'chunk_load_error'
  | 'render_error'
  | 'chunk_reload_auto'
  | 'chunk_reload_manual'
  | 'orphaned_session'
  | 'auth_me_error'
  /**
   * One step of the invitation → registration trace (XT-706). Exactly ONE kind
   * covers all of them; the step name travels in `meta.step`, so the two
   * allowlists (here and in the server handler) each gain one member instead of
   * a dozen that could drift apart.
   */
  | 'invite_trace';

export type ClientEventPayload = {
  kind: ClientEventKind;
  message?: string;
  name?: string;
  stack?: string;
  componentStack?: string;
  href?: string;
  pathname?: string;
  alreadyReloaded?: boolean;
  buildId?: string;
  meta?: Record<string, string | number | boolean | null>;
};

/** Same kind within this window is dropped (console still logs once). */
export const CLIENT_EVENT_DEBOUNCE_MS = 5_000;

const lastSentAtByKey = new Map<string, number>();

/**
 * Debounce key. Registration-trace steps all share one `kind`, and
 * `link.opened → signup.attempted → signup.stalled` happen inside a second —
 * keying on `kind` alone would drop two of those three. Keying on the step as
 * well is a no-op for every other kind, none of which set `meta.step`.
 */
const debounceKey = (payload: ClientEventPayload): string => {
  const step = payload.meta?.step;
  return typeof step === 'string' ? `${payload.kind}:${step}` : payload.kind;
};

const apiBase = (): string =>
  (import.meta.env.VITE_API_URL || 'http://localhost:3001').replace(/\/$/, '');

const buildPayload = (partial: ClientEventPayload): ClientEventPayload => ({
  ...partial,
  href:
    partial.href ??
    (typeof window !== 'undefined' ? window.location.href : undefined),
  pathname:
    partial.pathname ??
    (typeof window !== 'undefined' ? window.location.pathname : undefined),
  buildId:
    partial.buildId ??
    (typeof import.meta.env.VITE_GIT_SHA === 'string'
      ? import.meta.env.VITE_GIT_SHA
      : undefined),
});

const shouldSend = (key: string, now: number): boolean => {
  const prev = lastSentAtByKey.get(key);
  if (prev !== undefined && now - prev < CLIENT_EVENT_DEBOUNCE_MS) {
    return false;
  }
  lastSentAtByKey.set(key, now);
  return true;
};

/** Test-only: reset debounce state between cases. */
export const _resetClientEventDebounce = (): void => {
  lastSentAtByKey.clear();
};

/**
 * Best-effort report. Never throws. Prefer sendBeacon (survives unload);
 * fall back to keepalive fetch.
 */
export const reportClientEvent = (partial: ClientEventPayload): void => {
  try {
    const payload = buildPayload(partial);
    // Always mirror to the browser console for local / support screenshots.
    console.warn('[client-diagnostic]', payload.kind, payload);

    if (!shouldSend(debounceKey(payload), Date.now())) return;

    const url = `${apiBase()}/client-events`;
    const body = JSON.stringify(payload);

    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function'
    ) {
      const blob = new Blob([body], { type: 'application/json' });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }

    void fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit',
      mode: 'cors',
    }).catch(() => {
      // swallow — diagnostics must never break recovery
    });
  } catch {
    // swallow
  }
};
